import { Injectable, signal } from '@angular/core';

interface SingleTabLockRecord {
  tabId: string;
  lastSeen: number;
}

type SingleTabChannelMessage =
  | { type: 'ownership-check'; probeId: string; requesterTabId: string; }
  | { type: 'owner-present'; probeId: string; ownerTabId: string; }
  | { type: 'owner-alive'; ownerTabId: string; }
  | { type: 'owner-released'; ownerTabId: string; };

@Injectable({ providedIn: 'root' })
export class TabGuardService {
  readonly isCurrentTabBlocked = signal(false);

  private readonly lockStorageKey = 'siquote.single-tab.lock';
  private readonly tabSessionKey = 'siquote.single-tab.id';
  private readonly blockedSessionKey = 'siquote.single-tab.blocked';
  private readonly channelName = 'siquote.single-tab';
  private readonly heartbeatIntervalMs = 2000;
  private readonly staleThresholdMs = 30000;
  private readonly ownerResponseWaitMs = 1000;

  private readonly tabId = this.getOrCreateTabId();
  private channel: BroadcastChannel | null = null;
  private heartbeatTimerId: number | null = null;
  private staleCheckTimerId: number | null = null;
  private isInitialized = false;
  private isOwner = false;
  private activeProbeId: string | null = null;
  private activeProbeResolver: ((ownerPresent: boolean) => void) | null = null;

  private readonly boundBeforeUnload = () => this.releaseLock();
  // persisted=true means bfcache entry — do not release; the tab may be restored via pageshow
  private readonly boundPageHide = (event: PageTransitionEvent) => { if (!event.persisted) this.releaseLock(); };
  private readonly boundPageShow = (event: PageTransitionEvent) => { if (event.persisted) this.establishOwnership(); };
  private readonly boundStorageHandler = (event: StorageEvent) => this.handleStorageEvent(event);

  async initialize(): Promise<void> {
    if (this.isInitialized || typeof window === 'undefined' || this.isAuthHelperWindow()) {
      return;
    }

    // Drop any blocked flag persisted by older builds so it cannot force the blocked view on load
    try {
      sessionStorage.removeItem(this.blockedSessionKey);
    } catch {
      // sessionStorage unavailable
    }

    this.isInitialized = true;
    this.initializeBroadcastChannel();
    window.addEventListener('beforeunload', this.boundBeforeUnload);
    window.addEventListener('pagehide', this.boundPageHide);
    window.addEventListener('pageshow', this.boundPageShow);
    window.addEventListener('storage', this.boundStorageHandler);

    await this.establishOwnership();
  }

  dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.boundBeforeUnload);
      window.removeEventListener('pagehide', this.boundPageHide);
      window.removeEventListener('pageshow', this.boundPageShow);
      window.removeEventListener('storage', this.boundStorageHandler);
    }

    this.releaseLock();
    this.stopStaleCheck();
    this.channel?.close();
    this.channel = null;
    this.isInitialized = false;
  }

  private async establishOwnership(): Promise<void> {
    const existingLock = this.readLock();

    if (existingLock?.tabId === this.tabId) {
      this.becomeOwner();
      return;
    }

    if (existingLock && !this.isLockStale(existingLock)) {
      const ownerResponded = await this.probeForOwner();
      const latestLock = this.readLock();

      if (ownerResponded) {
        this.blockCurrentTab();
        return;
      }

      // No channel — probe always returns false, so fall back to lock freshness as the only ownership signal
      if (!this.channel && latestLock && latestLock.tabId !== this.tabId && !this.isLockStale(latestLock)) {
        this.blockCurrentTab();
        return;
      }
    }

    if (this.tryAcquireLock()) {
      this.becomeOwner();
      return;
    }

    const latestLock = this.readLock();
    if (latestLock?.tabId === this.tabId) {
      this.becomeOwner();
      return;
    }

    this.blockCurrentTab();
  }

  private initializeBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    this.channel = new BroadcastChannel(this.channelName);
    this.channel.onmessage = (event: MessageEvent<SingleTabChannelMessage>) => {
      this.handleChannelMessage(event.data);
    };
  }

  private handleChannelMessage(message: SingleTabChannelMessage): void {
    switch (message.type) {
      case 'ownership-check':
        if (this.isOwner) {
          this.postMessage({
            type: 'owner-present',
            probeId: message.probeId,
            ownerTabId: this.tabId
          });
        }
        break;
      case 'owner-present':
        if (message.probeId === this.activeProbeId && this.activeProbeResolver) {
          this.activeProbeResolver(true);
          this.activeProbeResolver = null;
          this.activeProbeId = null;
        }
        break;
      case 'owner-alive':
        if (!this.isOwner && message.ownerTabId !== this.tabId) {
          this.isCurrentTabBlocked.set(true);
        }
        break;
      case 'owner-released':
        break;
    }
  }

  private async probeForOwner(): Promise<boolean> {
    if (!this.channel) {
      return false;
    }

    return await new Promise<boolean>((resolve) => {
      const probeId = this.createUniqueId();
      this.activeProbeId = probeId;
      this.activeProbeResolver = resolve;

      this.postMessage({
        type: 'ownership-check',
        probeId,
        requesterTabId: this.tabId
      });

      setTimeout(() => {
        if (this.activeProbeId === probeId) {
          this.activeProbeId = null;
          this.activeProbeResolver = null;
          resolve(false);
        }
      }, this.ownerResponseWaitMs);
    });
  }

  private becomeOwner(): void {
    this.isOwner = true;
    this.stopStaleCheck();
    this.isCurrentTabBlocked.set(false);
    this.writeLock();
    this.startHeartbeat();
    this.postMessage({ type: 'owner-alive', ownerTabId: this.tabId });
  }

  private blockCurrentTab(): void {
    this.stopHeartbeat();
    this.isOwner = false;
    this.isCurrentTabBlocked.set(true);
    this.startStaleCheck();
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimerId = window.setInterval(() => {
      const currentLock = this.readLock();
      if (currentLock && currentLock.tabId !== this.tabId && !this.isLockStale(currentLock)) {
        this.blockCurrentTab();
        return;
      }

      this.writeLock();
      this.postMessage({ type: 'owner-alive', ownerTabId: this.tabId });
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimerId !== null) {
      window.clearInterval(this.heartbeatTimerId);
      this.heartbeatTimerId = null;
    }
  }

  private startStaleCheck(): void {
    this.stopStaleCheck();
    this.staleCheckTimerId = window.setInterval(() => {
      const lock = this.readLock();
      if (!lock || this.isLockStale(lock)) {
        this.establishOwnership();
      }
    }, this.heartbeatIntervalMs);
  }

  private stopStaleCheck(): void {
    if (this.staleCheckTimerId !== null) {
      window.clearInterval(this.staleCheckTimerId);
      this.staleCheckTimerId = null;
    }
  }

  private tryAcquireLock(): boolean {
    this.writeLock();
    return this.readLock()?.tabId === this.tabId;
  }

  private releaseLock(): void {
    this.stopHeartbeat();
    this.stopStaleCheck();

    if (!this.isOwner) {
      return;
    }

    // Remove our lock so the next page load (reload or MSAL login/logout redirect, which gets a new tabId) is not blocked by it
    if (this.readLock()?.tabId === this.tabId) {
      localStorage.removeItem(this.lockStorageKey);
    }

    this.isOwner = false;
    this.postMessage({ type: 'owner-released', ownerTabId: this.tabId });
  }

  private handleStorageEvent(event: StorageEvent): void {
    if (event.key !== this.lockStorageKey) {
      return;
    }

    const latestLock = this.parseLock(event.newValue);

    if (this.isOwner && latestLock && latestLock.tabId !== this.tabId && !this.isLockStale(latestLock)) {
      this.blockCurrentTab();
      return;
    }

    if (!this.isOwner && latestLock && latestLock.tabId !== this.tabId && !this.isLockStale(latestLock)) {
      this.isCurrentTabBlocked.set(true);
    }
  }

  private writeLock(): void {
    const record: SingleTabLockRecord = {
      tabId: this.tabId,
      lastSeen: Date.now()
    };

    localStorage.setItem(this.lockStorageKey, JSON.stringify(record));
  }

  private readLock(): SingleTabLockRecord | null {
    return this.parseLock(localStorage.getItem(this.lockStorageKey));
  }

  private parseLock(rawValue: string | null): SingleTabLockRecord | null {
    if (!rawValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as Partial<SingleTabLockRecord>;
      if (!parsed.tabId || typeof parsed.lastSeen !== 'number') {
        return null;
      }

      return {
        tabId: parsed.tabId,
        lastSeen: parsed.lastSeen
      };
    } catch {
      return null;
    }
  }

  private isLockStale(lock: SingleTabLockRecord): boolean {
    return Date.now() - lock.lastSeen > this.staleThresholdMs;
  }

  private getOrCreateTabId(): string {
    try {
      const existingTabId = sessionStorage.getItem(this.tabSessionKey);
      if (existingTabId && this.isReloadNavigation()) {
        return existingTabId;
      }

      const newTabId = this.createUniqueId();
      sessionStorage.setItem(this.tabSessionKey, newTabId);
      return newTabId;
    } catch {
      return this.createUniqueId();
    }
  }

  private isReloadNavigation(): boolean {
    if (typeof performance === 'undefined') {
      return false;
    }

    const navigationEntries = performance.getEntriesByType?.('navigation') ?? [];
    const navigationEntry = navigationEntries[0] as PerformanceNavigationTiming | undefined;
    return navigationEntry?.type === 'reload';
  }

  // MSAL popups (acquireTokenPopup) and iframes load this SPA too; they must not compete for the lock
  private isAuthHelperWindow(): boolean {
    try {
      return window.name.startsWith('msal.') || window.self !== window.top;
    } catch {
      return true;
    }
  }

  private createUniqueId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private postMessage(message: SingleTabChannelMessage): void {
    this.channel?.postMessage(message);
  }
}